import useIsMounted from "../../../../../../hooks/use-is-mounted";
import React, {useCallback} from "react";
import Api from "../../../../../../../core/services/api/api";
import {prepareDateRangeFilters} from "../form-sections/date-range";
import {Card, Typography} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../../../../../components/base/button/button";
import ExportingProgress from "../exporting-progress";

export const ExportDataViewCustomerTerritoryAndAccountManagerForm = ({fetchExportDataWithPagination, exportReport, exporting, progress}) => {
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => {
        return Yup.object().shape({
            keyword: Yup.string().trim(),
        })
    }, []);

    const initialValues = React.useMemo(() => {
        return {
            keyword: '',
        }
    }, []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Fetches the customer territory and account manager data for export
     * @type {(function(*, *): Promise<{hasNext: boolean, data: *[]} | undefined>)}
     */
    const fetchAndPrepareData = useCallback(async (filters, pagination) => {
        const response = await Api.listCustomersAccountManagersAndTerritories({
            keyword: filters.keyword,
            pagination: pagination,
        });
        if (!isMounted())
            return;
        const length = (response.data?.totalCount ?? 0);
        const hasNext = length > 0 && length > pagination.pageSize * pagination.currentPage;
        return {
            data: response.data?.items?.map((e, index) => ({
                "": (index + 1) + ((pagination.currentPage - 1) * pagination.pageSize),
                'Customer Code': `=${e.customerNo ?? ''}`,
                'Territory Code': e.territoryCode ?? '',
                'Account Manager Code': e.accountManagerCode ?? '',
            })) ?? [],
            hasNext,
            total: length,
        }
    }, [isMounted])

    /**
     * Exports the report based on the selected
     * @type {(function(*, *): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        const filters = {
            keyword: values.keyword,
            ...prepareDateRangeFilters(values),
        }
        await exportReport(async () => {
            const data = await fetchExportDataWithPagination(filters, fetchAndPrepareData, 100)
            return [data, "Customers' Territory and Account Managers"];
        });
    }, [exportReport, fetchExportDataWithPagination, fetchAndPrepareData])

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3, marginTop: 2}}>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{style: {width: '100%'}}}
                >
                    <Grid2 container spacing={3} justifyContent={'center'}>
                        <Grid2 xs={12} sx={{display: 'flex'}}>
                            <Typography variant={'subtitle1'} align={'center'}>
                                Select Filters for Customers' Territory and Account Managers Report
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12}>
                            <MuiRFF.TextField
                                name="keyword"
                                label="Keyword"
                                fullWidth
                                required={required}
                                size={'small'}
                            />
                        </Grid2>
                        <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBlockStart: 2}}>
                            <Button size={"small"} disabled={exporting} variant={'outlined'} color={'primary'} type={'submit'} sx={{minWidth: 200}}>
                                {exporting ? "Exporting..." : "Export"}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
                {
                    exporting &&
                    <ExportingProgress progress={progress}/>
                }
            </Card>
        </>
    );
}